import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import { News } from 'state';

/** A tag carried by the stories of a date range, with how many carry it. */
export type NewsTagFacet = {
  id: number;
  text: string;
  category: string | null;
  /** What an administrator reads ('United Kingdom (GB)'). */
  label?: string;
  /** What the tag is found by: its label and, for a country, aliases such as 'UK' or 'British'. */
  search?: string;
  count: number;
};

export type NewsPageQuery = {
  start_date: string;
  end_date: string;
  importance?: number;
  source?: 'all' | 'ai_curated' | 'original';
  /** Comma-separated tag ids; a story must carry every one. */
  tag_ids?: string;
  before_timestamp?: string;
  before_id?: number;
  limit?: number;
};

/** One slice of the news table: newest first and without bodies. */
export type NewsPage = {
  results: News[];
  has_more: boolean;
  /** Stories matching the filters; only on the first slice. */
  total: number | null;
  next: { before_timestamp: string; before_id: number } | null;
};

const errorText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(errorText).join(' ');
  if (value && typeof value === 'object')
    return Object.values(value).map(errorText).join(' ');
  return '';
};

async function call<T>(
  path: string,
  method: 'GET' | 'POST',
  data: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiCaller({
    apiUrl: API_URL,
    path,
    method,
    data,
    signal,
  });
  if (response.status < 200 || response.status >= 300)
    throw new Error(
      errorText(response.result) || 'The request failed. Please try again.',
    );
  return response.result as T;
}

export const fetchNewsPage = (query: NewsPageQuery, signal?: AbortSignal) =>
  call<NewsPage>('/admin/news_page', 'GET', query, signal);

export const fetchNewsTags = (
  range: { start_date: string; end_date: string },
  signal?: AbortSignal,
) =>
  call<{ results: NewsTagFacet[] }>('/admin/news_tags', 'GET', range, signal);

/** The whole story, body included: read when an administrator opens it. */
export const fetchNewsArticle = (id: number, signal?: AbortSignal) =>
  call<News>(`/news/${id}`, 'GET', {}, signal);

export const saveNews = (change: Partial<News> & { id: number }) =>
  call<News>('/admin/update_news', 'POST', change);
